import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getUsers,
  suspendUser,
  activateUser,
  exportUsers,
  createUser,
  impersonateUser,
  creditUser,
  AdminLicenseSelection,
} from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Ban,
  CheckCircle,
  Download,
  RefreshCw,
  UserPlus,
  X,
  Loader2,
  LogIn,
  AlertTriangle,
  Coins,
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  fullName: string;
  companyName: string;
  accountType: string;
  license?: AdminLicenseSelection | 'free' | 'commercial' | 'template_pack' | 'professional' | 'agency';
  creditsBalance?: number;
  role: string;
  subscriptionStatus: string;
  subscriptionExpiresAt: string;
  totalGenerations: number;
  monthlyLimit: number;
  generationsThisMonth: number;
  lastSignInAt: string;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const UserManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [search, setSearch] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Add User modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    email: '',
    password: '',
    fullName: '',
    companyName: '',
    license: 'free' as AdminLicenseSelection,
    accountType: 'user',
    credits: 0,
  });
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [addUserSuccess, setAddUserSuccess] = useState(false);

  // Impersonation modal
  const [impersonateModal, setImpersonateModal] = useState<{ userId: string; email: string; name: string } | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  // Bonus Credits modal
  const [bonusModal, setBonusModal] = useState<{ userId: string; name: string } | null>(null);
  const [bonusAmount, setBonusAmount] = useState<number>(100);
  const [addingBonus, setAddingBonus] = useState(false);
  const [bonusError, setBonusError] = useState<string | null>(null);
  const [bonusSuccess, setBonusSuccess] = useState(false);

  const handleOpenBonusModal = (user: User) => {
    setBonusModal({ userId: user.id, name: user.fullName || user.email });
    setBonusAmount(100);
    setBonusError(null);
    setBonusSuccess(false);
  };

  const handleAddBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bonusModal || bonusAmount <= 0) return;
    
    setAddingBonus(true);
    setBonusError(null);
    setBonusSuccess(false);
    
    try {
      await creditUser(bonusModal.userId, bonusAmount, 'admin_bonus');
      setBonusSuccess(true);
      await loadUsers();
      setTimeout(() => { setBonusModal(null); setBonusSuccess(false); }, 1500);
    } catch (err: any) {
      setBonusError(err.response?.data?.error || 'Failed to add bonus credits');
    } finally {
      setAddingBonus(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [pagination.page, accountTypeFilter, statusFilter, sortBy, sortOrder]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUsers({
        page: pagination.page,
        limit: pagination.limit,
        search,
        accountType: accountTypeFilter,
        status: statusFilter,
        sortBy,
        sortOrder
      });
      setUsers(data.users);
      setPagination(data.pagination);
      console.log(`Loaded ${data.users.length} users [[memory:5998053]]`);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadUsers();
  };

  const handleSuspend = async (userId: string) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    
    try {
      await suspendUser(userId);
      await loadUsers();
      console.log(`User ${userId} suspended [[memory:5998053]]`);
    } catch (err) {
      console.error('Error suspending user:', err);
      alert('Failed to suspend user');
    }
  };

  const handleActivate = async (userId: string) => {
    try {
      await activateUser(userId);
      await loadUsers();
      console.log(`User ${userId} activated [[memory:5998053]]`);
    } catch (err) {
      console.error('Error activating user:', err);
      alert('Failed to activate user');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportUsers({
        accountType: accountTypeFilter,
        status: statusFilter
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      console.log('Users exported [[memory:5998053]]');
    } catch (err) {
      console.error('Error exporting users:', err);
      alert('Failed to export users');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingUser(true);
    setAddUserError(null);
    setAddUserSuccess(false);
    try {
      await createUser({
        email: addUserForm.email,
        password: addUserForm.password || undefined,
        fullName: addUserForm.fullName || undefined,
        companyName: addUserForm.companyName || undefined,
        license: addUserForm.license,
        accountType: addUserForm.accountType,
        credits: addUserForm.credits > 0 ? addUserForm.credits : undefined,
      });
      setAddUserSuccess(true);
      // Reset form
      setAddUserForm({ email: '', password: '', fullName: '', companyName: '', license: 'free', accountType: 'user', credits: 0 });
      // Reload users list
      await loadUsers();
      // Auto-close after 1.5s
      setTimeout(() => { setShowAddModal(false); setAddUserSuccess(false); }, 1500);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to create user';
      setAddUserError(msg);
    } finally {
      setAddingUser(false);
    }
  };

  const handleImpersonate = async (user: User) => {
    setImpersonateModal({ userId: user.id, email: user.email, name: user.fullName || user.email });
    setImpersonateError(null);
  };

  const handleStartImpersonation = async () => {
    if (!impersonateModal) {
      return;
    }

    setImpersonating(true);
    setImpersonateError(null);

    try {
      const data = await impersonateUser(impersonateModal.userId);

      if (data.accessToken && data.refreshToken) {
        await startImpersonation({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          targetUserId: data.user?.id || impersonateModal.userId,
          targetEmail: data.user?.email || impersonateModal.email,
          targetName: data.user?.fullName || impersonateModal.name,
          adminReturnPath: `${location.pathname}${location.search}`,
        });

        window.location.assign('/dashboard');
        return;
      }

      if (data.url) {
        window.location.assign(data.url);
        return;
      }

      throw new Error('Impersonation response did not include a usable session');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to log in as this user';
      setImpersonateError(msg);
    } finally {
      setImpersonating(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => { setShowAddModal(true); setAddUserError(null); setAddUserSuccess(false); }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <UserPlus className="-ml-1 mr-2 h-4 w-4" />
            Add User
          </button>
          <button
            onClick={loadUsers}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className={`-ml-1 mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Download className="-ml-1 mr-2 h-4 w-4" />
            Export
          </button>
        </div>
      </div>

        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by name, email, or company..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Search
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <select
                  value={accountTypeFilter}
                  onChange={(e) => setAccountTypeFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">All Accounts</option>
                  <option value="user">User</option>
                  <option value="team">Team</option>
                  <option value="super_admin">Super Admin</option>
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="created_at">Created Date</option>
                  <option value="updated_at">Last Updated</option>
                  <option value="full_name">Name</option>
                  <option value="account_type">Account Type</option>
                </select>

                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </form>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">License</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Generations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.fullName || 'No name'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          {user.companyName && (
                            <div className="text-xs text-gray-400">{user.companyName}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.accountType === 'super_admin' ? 'bg-red-100 text-red-800' :
                            user.accountType === 'team' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.accountType}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 capitalize">{(user.license || 'free').replace('_', ' ')}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (user.creditsBalance || 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.creditsBalance ?? 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {(user.totalGenerations || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          This month: {user.generationsThisMonth || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800' :
                          user.subscriptionStatus === 'suspended' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.subscriptionStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateTime(user.lastSignInAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleImpersonate(user)}
                            className="text-amber-600 hover:text-amber-900"
                            title="Login as user"
                          >
                            <LogIn className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleOpenBonusModal(user)}
                            className="text-emerald-600 hover:text-emerald-900"
                            title="Add bonus credits"
                          >
                            <Coins className="h-4 w-4" />
                          </button>
                          {user.subscriptionStatus === 'active' ? (
                            <button
                              onClick={() => handleSuspend(user.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Suspend user"
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(user.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Activate user"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Impersonate User Modal */}
      {impersonateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setImpersonateModal(null)} />
            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <LogIn className="h-5 w-5 text-amber-600" />
                  Login as User
                </h3>
                <button onClick={() => setImpersonateModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">You are about to enter this user account.</p>
                  <p className="mt-1 text-xs">Your admin session will be backed up so you can return to the admin panel from inside the user dashboard.</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Target: <span className="font-medium text-gray-900">{impersonateModal.name}</span>
                </p>
                <p className="text-sm text-gray-500">{impersonateModal.email}</p>
              </div>

              {impersonating && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 text-amber-600 animate-spin" />
                  <span className="ml-2 text-sm text-gray-600">Switching into the user account...</span>
                </div>
              )}

              {impersonateError && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {impersonateError}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={handleStartImpersonation}
                  disabled={impersonating}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-lg hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Enter User Account
                </button>
                <button
                  onClick={() => setImpersonateModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddModal(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 z-10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-green-600" />
                  Add New User
                </h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {addUserSuccess && (
                <div className="mb-4 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" /> User created successfully!
                </div>
              )}

              {addUserError && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {addUserError}
                </div>
              )}

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={addUserForm.email}
                      onChange={(e) => setAddUserForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Password</label>
                    <input
                      type="password"
                      minLength={6}
                      value={addUserForm.password}
                      onChange={(e) => setAddUserForm(f => ({ ...f, password: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Leave empty to auto-generate"
                    />
                    <p className="mt-1 text-xs text-gray-400">Min. 6 characters. If left empty, a secure password will be generated and sent to the user.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Full Name</label>
                    <input
                      type="text"
                      value={addUserForm.fullName}
                      onChange={(e) => setAddUserForm(f => ({ ...f, fullName: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Company</label>
                    <input
                      type="text"
                      value={addUserForm.companyName}
                      onChange={(e) => setAddUserForm(f => ({ ...f, companyName: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Acme Inc."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">License</label>
                    <select
                      value={addUserForm.license}
                      onChange={(e) => setAddUserForm(f => ({ ...f, license: e.target.value as AdminLicenseSelection }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="free">Free</option>
                      <option value="commercial">Commercial (+275)</option>
                      <option value="template_pack">Commercial + Template Pack (+775)</option>
                      <option value="professional">Commercial + Professional (+1,000)</option>
                      <option value="agency">Commercial + Agency (+1,375)</option>
                      <option value="reseller">Commercial + Reseller (+275)</option>
                      <option value="bundle">Bundle (+2,600)</option>
                      <option value="fastpass">Commercial + Fastpass (+2,600)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Account Type</label>
                    <select
                      value={addUserForm.accountType}
                      onChange={(e) => setAddUserForm(f => ({ ...f, accountType: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="user">User</option>
                      <option value="team">Team</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Bonus Credits</label>
                    <input
                      type="number"
                      min={0}
                      value={addUserForm.credits}
                      onChange={(e) => setAddUserForm(f => ({ ...f, credits: parseInt(e.target.value) || 0 }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0"
                    />
                    <p className="mt-1 text-xs text-gray-400">Additional credits on top of license defaults</p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingUser}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {addingUser ? (
                      <><Loader2 className="-ml-1 mr-2 h-4 w-4 animate-spin" /> Creating...</>
                    ) : (
                      <><UserPlus className="-ml-1 mr-2 h-4 w-4" /> Create User</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Bonus Modal */}
      {bonusModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setBonusModal(null)} />
            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Coins className="h-5 w-5 text-emerald-600" />
                  Add Bonus Credits
                </h3>
                <button onClick={() => setBonusModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {bonusSuccess && (
                <div className="mb-4 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" /> Credits added successfully!
                </div>
              )}

              {bonusError && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {bonusError}
                </div>
              )}

              <div className="mb-4 text-sm text-gray-600">
                Adding bonus credits to <span className="font-semibold text-gray-900">{bonusModal.name}</span>
              </div>

              <form onSubmit={handleAddBonus}>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Amount</label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={bonusAmount}
                    onChange={(e) => setBonusAmount(parseInt(e.target.value) || 0)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">These will be labeled as bonus credits in the audit log.</p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setBonusModal(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingBonus || bonusAmount <= 0}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingBonus ? (
                      <><Loader2 className="-ml-1 mr-2 h-4 w-4 animate-spin" /> Adding...</>
                    ) : (
                      <><Coins className="-ml-1 mr-2 h-4 w-4" /> Add Credits</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
